import React, { useState } from 'react';
import { isSameDate as defaultIsSameDate, getSafeTime as defaultGetSafeTime } from '../lib/helpers';

// Helper to parse ordered_by and message from order notes
const defaultParseOrderNote = (note) => {
  if (!note) return { orderedBy: 'ไม่ระบุ', noteText: '' };
  if (typeof note === 'string' && note.startsWith("ordered_by:")) {
    const pipeIdx = note.indexOf("|note:");
    if (pipeIdx !== -1) {
      const orderedBy = note.substring(11, pipeIdx);
      const noteText = note.substring(pipeIdx + 6);
      return { orderedBy, noteText };
    }
  }
  return { orderedBy: 'ไม่ระบุ', noteText: note };
};

// Helper to parse database image_url column into full and zoom URLs
const defaultParseImages = (imageUrlField) => {
  if (!imageUrlField) return { full: '', zoom: '', other: '', document: '' };
  if (typeof imageUrlField !== 'string') return { full: '', zoom: '', other: '', document: '' };

  if (imageUrlField.startsWith('{') && imageUrlField.endsWith('}')) {
    try {
      const parsed = JSON.parse(imageUrlField);
      return {
        full: parsed.full || '',
        zoom: parsed.zoom || '',
        other: parsed.other || '',
        document: parsed.document || ''
      };
    } catch (e) {
      return { full: imageUrlField, zoom: '', other: '', document: '' };
    }
  }
  return { full: imageUrlField, zoom: '', other: '', document: '' };
};

const ClinicOrderPage = ({
  isGuestMode = false,
  targetWarehouse = '',
  setTargetWarehouse,
  WAREHOUSES_LIST = [],
  clinicOrderSearchQuery = '',
  setClinicOrderSearchQuery,
  itemMaster = [],
  instruments = [],
  supplies = [],
  orderBasket = [],
  setOrderBasket,
  orderNote = '',
  setOrderNote,
  orderedByName = '',
  setOrderedByName,
  user = null,
  handleSendOrder,
  handleOrderQuickAdd,
  handleOrderQuickRemove,
  orders = [],
  isSameDate = defaultIsSameDate,
  handleCancelOrder,
  parseOrderNote = defaultParseOrderNote,
  showPreview = {},
  handleTogglePreview,
  parseImages = defaultParseImages,
  getUserNameByEmail,
  getSafeTime = defaultGetSafeTime,
  clinicOrderHistoryDate,
  setClinicOrderHistoryDate,
  isCartModalOpen,
  setIsCartModalOpen
}) => {
  const [internalIsCartModalOpen, setInternalIsCartModalOpen] = useState(false);
  const isCartOpen = isCartModalOpen !== undefined ? isCartModalOpen : internalIsCartModalOpen;
  const setCartOpen = setIsCartModalOpen || setInternalIsCartModalOpen;

  const [internalHistoryDate, setInternalHistoryDate] = useState(new Date().toISOString().split('T')[0]);
  const historyDate = clinicOrderHistoryDate !== undefined ? clinicOrderHistoryDate : internalHistoryDate;
  const setHistoryDate = setClinicOrderHistoryDate || setInternalHistoryDate;

  const resolveUserName = getUserNameByEmail || ((email) => email || 'ไม่ระบุ');
  const checkSameDate = isSameDate || defaultIsSameDate;
  const checkSafeTime = getSafeTime || defaultGetSafeTime;
  const parseNote = parseOrderNote || defaultParseOrderNote;
  const parseImgs = parseImages || defaultParseImages;

  const resolveImageUrl = (url) => {
    if (!url) return '';
    const parsed = parseImgs(url);
    return parsed.zoom || parsed.full || url;
  };

  const onQuickAdd = (masterLot) => {
    if (handleOrderQuickAdd) {
      handleOrderQuickAdd(masterLot);
    } else if (setOrderBasket) {
      setOrderBasket(prev => {
        const exist = prev.find(x => x.name === masterLot.item_name);
        if (exist) return prev.map(x => x.name === masterLot.item_name ? { ...x, qty: x.qty + 1 } : x);
        return [...prev, { name: masterLot.item_name, qty: 1 }];
      });
    }
  };

  const onQuickRemove = (it) => {
    if (handleOrderQuickRemove) {
      handleOrderQuickRemove(it);
    } else if (setOrderBasket) {
      setOrderBasket(prev => prev.map(x => x.name === it.name ? { ...x, qty: x.qty - 1 } : x).filter(x => x.qty > 0));
    }
  };

  const onQuickIncrement = (it, availableQty) => {
    if (it.qty < availableQty && setOrderBasket) {
      setOrderBasket(prev => prev.map(x => x.name === it.name ? { ...x, qty: x.qty + 1 } : x));
    }
  };

  const onSubmitOrder = async () => {
    if (handleSendOrder) {
      const res = await handleSendOrder();
      if (res !== false) {
        setCartOpen(false);
      }
    }
  };

  // Filter items
  const filteredItems = (itemMaster || [])
    .filter(masterLot => masterLot.category === 'วัสดุคงทน' || masterLot.category === 'หัวกรอ' || masterLot.category === 'กล่องหัวกรอ' || masterLot.is_set)
    .filter(masterLot => {
      if (!clinicOrderSearchQuery) return true;
      const nameMatches = masterLot.item_name.toLowerCase().includes(clinicOrderSearchQuery.toLowerCase());
      const aliasMatches = masterLot.aliases && masterLot.aliases.toLowerCase().includes(clinicOrderSearchQuery.toLowerCase());
      return nameMatches || aliasMatches;
    });

  // Separate available and unavailable
  const availableItems = [];
  const unavailableItems = [];

  filteredItems.forEach(masterLot => {
    let availableQty = 0;
    if (masterLot.category === 'กล่องหัวกรอ') {
      availableQty = (instruments || []).filter(i => i.master_id === masterLot.id && i.is_active && i.status === 'sterile-ready').length;
    } else {
      const sterileSupply = (supplies || []).find(s => s.master_id === masterLot.id && s.warehouse_name.includes("ห้อง sterile"));
      availableQty = sterileSupply ? sterileSupply.item_amount : 0;
    }

    const currentCartItem = (orderBasket || []).find(x => x.name === masterLot.item_name);
    const currentCartQty = currentCartItem ? currentCartItem.qty : 0;

    const itemWithQty = {
      ...masterLot,
      availableQty,
      currentCartQty
    };

    if (availableQty > 0) {
      availableItems.push(itemWithQty);
    } else {
      unavailableItems.push(itemWithQty);
    }
  });

  // Group by category helper
  const groupByCategory = (items) => {
    const groups = {
      doctorBoxes: [],
      sets: [],
      handpieces: [],
      durable: []
    };

    items.forEach(item => {
      if (item.category === 'กล่องหัวกรอ') {
        groups.doctorBoxes.push(item);
      } else if (item.is_set) {
        groups.sets.push(item);
      } else if (item.category === 'หัวกรอ') {
        groups.handpieces.push(item);
      } else if (item.category === 'วัสดุคงทน') {
        groups.durable.push(item);
      }
    });

    return groups;
  };

  const availableGrouped = groupByCategory(availableItems);
  const unavailableGrouped = groupByCategory(unavailableItems);

  const hasAvailableItems =
    availableGrouped.doctorBoxes.length > 0 ||
    availableGrouped.sets.length > 0 ||
    availableGrouped.handpieces.length > 0 ||
    availableGrouped.durable.length > 0;

  const hasUnavailableItems =
    unavailableGrouped.doctorBoxes.length > 0 ||
    unavailableGrouped.sets.length > 0 ||
    unavailableGrouped.handpieces.length > 0 ||
    unavailableGrouped.durable.length > 0;

  const renderItemCard = (masterLot) => {
    const availableQty = masterLot.availableQty;
    const currentCartQty = masterLot.currentCartQty;
    const previewKey = `clinic_order_${masterLot.id || masterLot.item_name}`;

    return (
      <div key={masterLot.id} className={`clinic-order-item${masterLot.is_set ? ' is-set' : ''}`}>
        <div className="clinic-order-item-top">
          <div className="clinic-order-item-name">
            {masterLot.is_set && (
              <span className={`set-badge ${masterLot.category === 'กล่องหัวกรอ' ? 'box' : 'set'}`}>
                {masterLot.category === 'กล่องหัวกรอ' ? '🧰 กล่อง' : '📦 ชุด'}
              </span>
            )}
            {masterLot.item_name}
            <br />
            <span className={`clinic-order-qty-badge ${availableQty > 0 ? 'available' : 'unavailable'}`}>
              {availableQty > 0 ? '✓' : '✕'} พร้อมจ่าย: {availableQty}
            </span>
          </div>
          <div className="clinic-order-item-actions">
            <button
              className="clinic-order-add-btn"
              onClick={() => onQuickAdd(masterLot)}
              disabled={availableQty <= 0 || currentCartQty >= availableQty}
            >
              + สั่ง
            </button>
          </div>
        </div>

        {masterLot.image_url && (
          <div
            className="clinic-order-image-preview"
            onClick={() => handleTogglePreview && handleTogglePreview(previewKey)}
            style={{ cursor: handleTogglePreview ? 'pointer' : 'default' }}
          >
            <img
              src={resolveImageUrl(masterLot.image_url)}
              alt={masterLot.item_name}
              style={{ maxHeight: showPreview?.[previewKey] ? '180px' : '80px', objectFit: 'contain', transition: 'max-height 0.2s' }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="clinic-order-container">
        <div className="clinic-order-card">
          {/* Header */}
          <div className="clinic-order-header">
            <h2>🛒 สั่งเครื่องมือและวัสดุ</h2>
            <p>เลือกห้องและรายการที่ต้องการสั่ง</p>
            <div className="accent-bar"></div>
          </div>

          {/* Guest mode warning */}
          {isGuestMode && (
            <div className="clinic-order-guest-banner">
              🔒 คุณอยู่ในโหมดผู้ใช้งานทั่วไป (Guest Mode) ของห้อง: "{targetWarehouse?.trim()}" ไม่สามารถเปลี่ยนห้องสั่งของได้
            </div>
          )}

          {/* Room selector */}
          <select
            className="clinic-order-select"
            value={targetWarehouse || ''}
            onChange={(e) => setTargetWarehouse && setTargetWarehouse(e.target.value)}
            disabled={isGuestMode}
          >
            <option value="">-- ระบุห้องของคุณ --</option>
            {(WAREHOUSES_LIST || []).filter(w => {
              const exclude = [
                "คลังใหญ่ (คลังพัสดุ)",
                "คลังย่อย (คลังห้องฟัน)",
                "ห้องเก็บของ",
                "ห้องคลีนิก",
                "   - ห้อง sterile",
                "   - กำลัง sterile",
                "   - ห้องล้างเครื่องมือ"
              ];
              return !exclude.includes(w.name);
            }).map(w => <option key={w.name} value={w.name}>{w.name.trim()}</option>)}
          </select>

          {/* Search input */}
          <div className="clinic-order-search-container">
            <input
              type="text"
              className="clinic-order-search-input"
              placeholder="🔍 ค้นหาเครื่องมือหรือวัสดุ..."
              value={clinicOrderSearchQuery || ''}
              onChange={(e) => setClinicOrderSearchQuery && setClinicOrderSearchQuery(e.target.value)}
            />
            {clinicOrderSearchQuery && (
              <button className="clinic-order-search-clear-btn" onClick={() => setClinicOrderSearchQuery && setClinicOrderSearchQuery('')}>✕</button>
            )}
          </div>

          {/* Items listing */}
          <div className="clinic-order-grouped-sections">
            {/* 1. Available Items */}
            {hasAvailableItems && (
              <div className="clinic-order-status-group available">
                <div className="clinic-order-status-header">
                  <span className="status-indicator dot-green"></span>
                  <h3>รายการที่พร้อมจ่าย (พร้อมจัดส่งทันที)</h3>
                </div>

                {availableGrouped.doctorBoxes.length > 0 && (
                  <div className="clinic-order-category-section">
                    <h4 className="clinic-order-category-title">🧰 กล่องหมอ ({availableGrouped.doctorBoxes.length})</h4>
                    <div className="clinic-order-grid">
                      {availableGrouped.doctorBoxes.map(renderItemCard)}
                    </div>
                  </div>
                )}

                {availableGrouped.sets.length > 0 && (
                  <div className="clinic-order-category-section">
                    <h4 className="clinic-order-category-title">📦 เซทเครื่องมือ ({availableGrouped.sets.length})</h4>
                    <div className="clinic-order-grid">
                      {availableGrouped.sets.map(renderItemCard)}
                    </div>
                  </div>
                )}

                {availableGrouped.handpieces.length > 0 && (
                  <div className="clinic-order-category-section">
                    <h4 className="clinic-order-category-title">🦷 หัวกรอและอุปกรณ์ ({availableGrouped.handpieces.length})</h4>
                    <div className="clinic-order-grid">
                      {availableGrouped.handpieces.map(renderItemCard)}
                    </div>
                  </div>
                )}

                {availableGrouped.durable.length > 0 && (
                  <div className="clinic-order-category-section">
                    <h4 className="clinic-order-category-title">🏢 วัสดุคงทน ({availableGrouped.durable.length})</h4>
                    <div className="clinic-order-grid">
                      {availableGrouped.durable.map(renderItemCard)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 2. Unavailable Items */}
            {hasUnavailableItems && (
              <div className="clinic-order-status-group unavailable">
                <div className="clinic-order-status-header">
                  <span className="status-indicator dot-red"></span>
                  <h3>รายการที่ไม่พร้อมจ่าย (หมดชั่วคราว)</h3>
                </div>

                {unavailableGrouped.doctorBoxes.length > 0 && (
                  <div className="clinic-order-category-section">
                    <h4 className="clinic-order-category-title">🧰 กล่องหมอ ({unavailableGrouped.doctorBoxes.length})</h4>
                    <div className="clinic-order-grid">
                      {unavailableGrouped.doctorBoxes.map(renderItemCard)}
                    </div>
                  </div>
                )}

                {unavailableGrouped.sets.length > 0 && (
                  <div className="clinic-order-category-section">
                    <h4 className="clinic-order-category-title">📦 เซทเครื่องมือ ({unavailableGrouped.sets.length})</h4>
                    <div className="clinic-order-grid">
                      {unavailableGrouped.sets.map(renderItemCard)}
                    </div>
                  </div>
                )}

                {unavailableGrouped.handpieces.length > 0 && (
                  <div className="clinic-order-category-section">
                    <h4 className="clinic-order-category-title">🦷 หัวกรอและอุปกรณ์ ({unavailableGrouped.handpieces.length})</h4>
                    <div className="clinic-order-grid">
                      {unavailableGrouped.handpieces.map(renderItemCard)}
                    </div>
                  </div>
                )}

                {unavailableGrouped.durable.length > 0 && (
                  <div className="clinic-order-category-section">
                    <h4 className="clinic-order-category-title">🏢 วัสดุคงทน ({unavailableGrouped.durable.length})</h4>
                    <div className="clinic-order-grid">
                      {unavailableGrouped.durable.map(renderItemCard)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty search state */}
            {availableItems.length === 0 && unavailableItems.length === 0 && (
              <div className="clinic-order-no-items">
                🔍 ไม่พบรายการเครื่องมือหรือวัสดุที่ค้นหา
              </div>
            )}
          </div>

          {/* Order history */}
          <div className="clinic-order-history">
            <div className="clinic-order-history-header">
              <h3>🗂️ ประวัติคำสั่งซื้อ {targetWarehouse ? `ของ ${targetWarehouse}` : ''}</h3>
              {targetWarehouse && (
                <input
                  type="date"
                  className="clinic-order-history-date"
                  value={historyDate}
                  onChange={(e) => setHistoryDate(e.target.value)}
                />
              )}
            </div>

            {!targetWarehouse ? (
              <div className="clinic-order-history-empty">กรุณาระบุห้องด้านบนเพื่อดูประวัติ</div>
            ) : (
              <div className="clinic-order-history-list">
                {(orders || []).filter(o => o.clinic_name === targetWarehouse && checkSameDate(o.timestamp, historyDate)).length === 0 ? (
                  <div className="clinic-order-history-empty">ไม่มีประวัติการสั่งของในวันที่เลือก</div>
                ) : (
                  (orders || [])
                    .filter(o => o.clinic_name === targetWarehouse && checkSameDate(o.timestamp, historyDate))
                    .sort((a, b) => checkSafeTime(b.timestamp) - checkSafeTime(a.timestamp))
                    .map(order => {
                      const isPending = order.status === 'pending';
                      const isCompleted = order.status === 'completed';
                      const isCancelled = order.status === 'cancelled';

                      return (
                        <div key={order.id} className={`clinic-order-history-card ${order.status}`}>
                          <div className="clinic-order-history-card-top">
                            <div>
                              <div className="clinic-order-history-time">⏰ {new Date(order.timestamp).toLocaleString('th-TH')}</div>
                              {(() => {
                                const parsed = parseNote(order.note);
                                return (
                                  <div className="clinic-order-history-note" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <div>👤 ผู้สั่ง: <strong>{resolveUserName(parsed.orderedBy)}</strong></div>
                                    {parsed.noteText && <div>📝 ข้อความ: {parsed.noteText}</div>}
                                  </div>
                                );
                              })()}
                            </div>
                            <div>
                              {isPending && <span className="clinic-order-status-badge pending">⏳ กำลังเตรียม</span>}
                              {isCompleted && (
                                <>
                                  <span className="clinic-order-status-badge completed">✅ สำเร็จ</span>
                                  {order.cancel_reason && <div className="clinic-order-completed-warning">⚠️ {order.cancel_reason}</div>}
                                </>
                              )}
                              {isCancelled && <span className="clinic-order-status-badge cancelled">❌ ยกเลิกแล้ว</span>}
                            </div>
                          </div>

                          <div className={`clinic-order-history-items${isCancelled ? ' cancelled' : ''}`}>
                            {order.items.map((it, i) => {
                              const dispensedQty = it.dispensed_qty !== undefined ? it.dispensed_qty : it.qty;
                              const isFulfilled = dispensedQty >= it.qty;
                              return (
                                <div key={i} className="clinic-order-history-item-row">
                                  <span>{isCompleted ? (isFulfilled ? '✅' : '❌') : '—'}</span>
                                  <span>
                                    {it.name}
                                    {isCompleted && !isFulfilled ? ` (สั่ง ${it.qty}, ได้ ${dispensedQty})` : ` (${it.qty})`}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          {isCancelled && order.cancel_reason && <div className="clinic-order-cancel-reason">เหตุผล: {order.cancel_reason}</div>}

                          {isPending && (
                            <div className="clinic-order-cancel-btn">
                              <button onClick={() => handleCancelOrder && handleCancelOrder(order.id, order.clinic_name)}>
                                ❌ ยกเลิกคำสั่งซื้อนี้
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Action Button (FAB) for Cart */}
      <button
        className={`clinic-order-fab-cart${(orderBasket || []).length === 0 ? ' empty' : ''}`}
        onClick={() => setCartOpen(true)}
        title="ดูตะกร้าคำสั่งซื้อ"
      >
        <span className="fab-icon">🛒</span>
        {(orderBasket || []).length > 0 && (
          <span className="fab-badge">
            {(orderBasket || []).reduce((sum, item) => sum + item.qty, 0)}
          </span>
        )}
      </button>

      {/* Cart Modal Popup */}
      {isCartOpen && (
        <div className="clinic-order-modal-overlay" onClick={() => setCartOpen(false)}>
          <div className="clinic-order-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="clinic-order-modal-header">
              <h3>🛒 ตะกร้าที่สั่ง</h3>
              <button className="clinic-order-modal-close" onClick={() => setCartOpen(false)}>✕</button>
            </div>

            <div className="clinic-order-modal-body">
              {(orderBasket || []).length === 0 ? (
                <div className="clinic-order-cart-empty">ยังไม่มีรายการในตะกร้า — เลือกรายการด้านบนเพื่อเพิ่ม</div>
              ) : (
                <div className="clinic-order-modal-items">
                  {(orderBasket || []).map((it, idx) => {
                    const masterItem = (itemMaster || []).find(masterLot => masterLot.item_name === it.name);

                    let availableQty = 0;
                    if (masterItem) {
                      if (masterItem.category === 'กล่องหัวกรอ') {
                        availableQty = (instruments || []).filter(i => i.master_id === masterItem.id && i.is_active && i.status === 'sterile-ready').length;
                      } else {
                        const sterileSupply = (supplies || []).find(s => s.master_id === masterItem.id && s.warehouse_name.includes("ห้อง sterile"));
                        availableQty = sterileSupply ? sterileSupply.item_amount : 0;
                      }
                    }

                    return (
                      <div key={idx} className="clinic-order-cart-item">
                        <span className="clinic-order-cart-item-name">{it.name}</span>
                        <div className="clinic-order-cart-controls">
                          <button className="clinic-order-cart-qty-btn minus" onClick={() => onQuickRemove(it)}>−</button>
                          <span className="clinic-order-cart-qty">{it.qty}</span>
                          <button className="clinic-order-cart-qty-btn plus" onClick={() => onQuickIncrement(it, availableQty)} disabled={it.qty >= availableQty}>+</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="clinic-order-modal-note-section">
                {setOrderedByName && (
                  <div style={{ marginBottom: '8px' }}>
                    <label className="clinic-order-modal-label">ชื่อผู้สั่ง:</label>
                    <input
                      type="text"
                      className="clinic-order-search-input"
                      placeholder={user?.email ? `ชื่อผู้สั่ง (ค่าเริ่มต้น: ${user.email})` : "ระบุชื่อผู้สั่ง..."}
                      value={orderedByName || ''}
                      onChange={(e) => setOrderedByName(e.target.value)}
                      style={{ marginTop: '4px', fontSize: '0.95em' }}
                    />
                  </div>
                )}
                <label className="clinic-order-modal-label">ข้อความถึงห้องจ่ายของ (ถ้ามี)...</label>
                <textarea
                  className="clinic-order-note"
                  placeholder="พิมพ์ข้อความที่ต้องการแจ้งเตือน เช่น ฝากส่งด่วนพิเศษ หรือระบุรายละเอียดเพิ่มเติม..."
                  value={orderNote || ''}
                  onChange={(e) => setOrderNote && setOrderNote(e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="clinic-order-modal-footer">
              <button className="clinic-order-modal-cancel-btn" onClick={() => setCartOpen(false)}>ปิด</button>
              <button
                className="clinic-order-submit-btn"
                onClick={onSubmitOrder}
                disabled={(orderBasket || []).length === 0}
              >
                🚀 ส่งใบสั่งของ
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClinicOrderPage;
